/*
 * BOARD METRICS — how alive this screen actually is, measured, not implied.
 *
 * The header used to badge the tile count, which never changes while you look
 * at it and answers no question a person standing at a wall display has. The
 * questions they do have: is data still flowing, how fast, and how far behind
 * the plant am I looking? Those are measurable at the one chokepoint every
 * value already passes — the host's frame handler — so this is a counter, not
 * a subsystem.
 *
 * LATENCY IS CROSS-CLOCK, and the number is presented accordingly. A value
 * carries its producer's timestamp (backend clock); "now" is the browser's.
 * The difference includes genuine transport plus whatever the two clocks
 * disagree by, so it is displayed rounded and honest ("~"), never as a
 * precision figure. It still does the job that matters: a board whose lag
 * jumps from ~40 ms to ~4 s is telling you something real regardless of a
 * constant clock offset, because the OFFSET does not move — the transport did.
 *
 * A module-level meter, keyed per board, because the hosts that record and the
 * page that reads are far apart in the tree, and threading a meter through
 * every layer would put plumbing in five files to move two numbers.
 */

const WINDOW_MS = 5_000;

/*
 * Deliberately simple arithmetic: each batch is one entry carrying its own
 * count, and every read recomputes from the entries still inside the window.
 * Nothing accumulates, so nothing can drift.
 */
interface Batch {
  atMs: number;
  count: number;
  lagMs: number;
}

class WindowMeter {
  private batches: Batch[] = [];
  /*
   * Whether a value has EVER reached this board (this page-visit) — set once,
   * never cleared. The rate window above empties after WINDOW_MS of no delivery,
   * which for a steady value (an industrial reading that simply is not changing)
   * is the normal case, not a fault. Health keys off THIS instead of the rate, so
   * a bound board that is delivering current values reads live even while the
   * value holds still; the rate number stays honestly 0/s in that gap.
   */
  private ever = false;

  record(count: number, lagMs: number): void {
    this.ever = true;
    this.batches.push({ atMs: Date.now(), count, lagMs });
  }

  hasEver(): boolean {
    return this.ever;
  }

  read(): { perSecond: number; lagMs: number } | undefined {
    const now = Date.now();
    this.batches = this.batches.filter((b) => b.atMs >= now - WINDOW_MS);
    if (this.batches.length === 0) return undefined;
    let total = 0;
    for (const b of this.batches) total += b.count;
    // Median over STAMPED batches only: a producer that didn't timestamp counts
    // for rate but must not drag the latency figure toward zero (or claim one at
    // all when nothing was stamped — lagMs 0 then means "unmeasured").
    const lags = this.batches.map((b) => b.lagMs).filter((l) => l > 0).sort((a, b) => a - b);
    const spanMs = Math.max(now - this.batches[0].atMs, 1000);
    return {
      perSecond: total / (Math.min(spanMs, WINDOW_MS) / 1000),
      lagMs: lags.length ? lags[Math.floor(lags.length / 2)] : 0,
    };
  }
}

const meters = new Map<string, WindowMeter>();

/** Record a delivered value batch for a board. `producerTsMs` may be 0/absent
 *  when the producer did not stamp; those batches count for rate, not lag. */
export function recordValues(boardKey: string, count: number, newestProducerTsMs: number): void {
  if (count <= 0) return;
  let m = meters.get(boardKey);
  if (!m) {
    m = new WindowMeter();
    meters.set(boardKey, m);
  }
  m.record(count, newestProducerTsMs > 0 ? Math.max(0, Date.now() - newestProducerTsMs) : 0);
}

/** The board's live rate + lag, or undefined when nothing has flowed lately. */
export function readMetrics(boardKey: string): { perSecond: number; lagMs: number } | undefined {
  return meters.get(boardKey)?.read();
}

/** Whether this board has ever delivered a value this visit. Health uses it so a
 *  steady-but-connected board (values current, none changing right now) reads
 *  live rather than flipping to stale the moment the rate window empties. */
export function boardEverLive(boardKey: string): boolean {
  return meters.get(boardKey)?.hasEver() ?? false;
}

/** A board's meter is dropped when its page unmounts; the map must not hold a
 *  meter per board ever visited for the life of the tab. */
export function dropMetrics(boardKey: string): void {
  meters.delete(boardKey);
}
